import { Component, Inject } from "@angular/core";
import {
    NmsWizardStepModel,
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import {
    ANGULARJS_SCOPE,
    ANGULARJS_TRANSLATE,
    LABEL_COLOR_SERVICE} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import {
    IntermediatesStepModel,
    IntermediateDevice,
    IntermediatesChangesToBeApplied,
    IntermediatesChangesCallbacks
} from "./intermediates-step.model";
import { ColumnConfigType, SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import { Device } from "@nms-ng2/app/modules/device/device-backup/device-backup.models";
import { DialogService } from "@nms-ng2/app/shared/services/dialog/dialog.service";
import { DeviceModalOpenService } from "@nms-ng2/app/shared/services/devices/device-modal-open.service";
import { STATUS } from "@nms-ng2/app/modules/topology/links/links.model";
import { PRIMARY_PRESSED_COLOR } from "@nms-ng2/app/shared/constants/colors";
import { IntermediatesStepService } from "./services/intermediates-step.service";
import { DwdmCircuitWizardValidationUtilsService } from "../../../services/dwdm-circuit-wizard-validation-utils.service";
import { CircuitValidationService } from "../../../services/circuit-validation.service";
import { EndsStepModel } from "../1-ends/ends-step.model";
import { TranspondersStepModel } from "../2-transponders/transponders-step.model";
import { CircuitDiagram } from "../../../diagram/models/diagram.models";
import { DwdmDiagramWizardConverterService } from "../../diagram/dwdm-diagram-wizard-converter.service";
import { LinksStepModel } from "../4-links/links-step.model";
import { AmplifiersStepModel } from "../5-amplifiers/amplifiers-step.model";
import { OscManagementStepModel } from "../6-osc-management/osc-management-step.model";
import { GeneralStepModel } from "../7-general/general-step.model";
import { DwdmCircuitWizardUtilsService } from "../../utils/dwdm-circuit-wizard-utils.service";
import { NmsToggleComponent } from "@nms-angular-toolkit/nms-toggle";

/**
 * Step Intermediates do DwdmCircuitWizard.
 */
@Component({
    selector: "intermediates-step",
    templateUrl: "./intermediates-step.component.html",
    styleUrls: ["./intermediates-step.component.scss"]
})
export class IntermediatesStepComponent extends AbstractNmsWizardStep {

    /**
     * Cópia do modelo atrelado ao HTML para podermos controlar quando as mudanças devem refletir no formulário.
     * Quando é detectado que uma mudança causa invalidação nos passos posteriores, só podemos refletir a mudança
     * quando o usuário aceitar a invalidação dos próximos passos. Devido ao uso do Nms-Toggle, foi preciso realizar
     * esse controle manual.
     */
    pendingModel = new IntermediatesStepModel();
    model: IntermediatesStepModel;

    endsModel: EndsStepModel;
    transpondersModel: TranspondersStepModel;
    linksModel: LinksStepModel;
    amplifiersModel: AmplifiersStepModel;
    oscsModel: OscManagementStepModel;
    generalModel: GeneralStepModel;

    useIntermediateDevices: boolean = false;
    activeDevices: Device[] = [];
    tableData: any[] = [];

    devicesTableColumns: SimpleTableColumnConfig[] = [];

    columnsSortable: Record<string, boolean> = {
        name: true,
        hostname: true,
        serialNumber: true,
        firmware: true,
        location: true,
        lastUpdate: true,
        status: true
    };

    aEndResourceId: number;
    zEndResourceId: number;

    circuitDiagram: CircuitDiagram;

    /**
     * Propriedade que mantém a última modificação do usuário armazenada
     * para posterior aplicação na função @see processChanges.
     */
    lastChangeToBeApplied: IntermediatesChangesToBeApplied = null;

    /**
     * Ações que devem ser realizadas para cada tipo de modificação que o usuário pode realizar neste step.
     */
    changesCallbacks: IntermediatesChangesCallbacks = {
        addIntermediate: this.addIntermediate.bind(this),
        removeIntermediate: this.removeIntermediate.bind(this),
        removeAllIntermediate: this.removeAllIntermediate.bind(this),
        changeUseIntermediate: this.changeUseIntermediate.bind(this)
    };

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            @Inject(LABEL_COLOR_SERVICE) private readonly labelColorService: any,
            private readonly dialogService: DialogService, private readonly deviceUtilsService: DeviceModalOpenService,
            @Inject(ANGULARJS_SCOPE) private readonly $scope: any,
            private readonly intermediatesStepService: IntermediatesStepService,
            private readonly circuitValidationService: CircuitValidationService,
            private readonly circuitWizardValidationUtilsService: DwdmCircuitWizardValidationUtilsService,
            private readonly diagramConverterService: DwdmDiagramWizardConverterService,
            private readonly circuitWizardUtilsService: DwdmCircuitWizardUtilsService) {
        super();
        this.model = new IntermediatesStepModel();
        this.model.useIntermediateDevices = false;
    }

    private async initializeTableColumns(): Promise<void> {
        this.devicesTableColumns = [
            {
                field: "name",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.name"),
                width: "350px",
                optionalColumn: { type: ColumnConfigType.TEXT },
                customColor: PRIMARY_PRESSED_COLOR,
            },
            {
                field: "hostname",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.hostname"),
                optionalColumn: { type: ColumnConfigType.TEXT },
            },
            {
                field: "serialNumber",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.serialNumber"),
                optionalColumn: { type: ColumnConfigType.TEXT },
            },
            {
                field: "firmware",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.firmware"),
                optionalColumn: { type: ColumnConfigType.TEXT }
            },
            {
                field: "location",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.locale"),
                optionalColumn: { type: ColumnConfigType.TEXT },
            },
            {
                field: "lastUpdate",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.lastUpdate"),
                optionalColumn: { type: ColumnConfigType.TEXT },
            },
            {
                field: "status",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.status"),
                optionalColumn: {
                    type: ColumnConfigType.STATUS,
                    convert: (status) => {
                        const { translationKey, bgColor } = STATUS[status.trim().toUpperCase()];
                        const color = this.labelColorService.getBestColorContrastForForeground(bgColor);
                        const label = this.translate.instant(translationKey);

                        return { label, bgColor, color };
                    }
                }
            },
            {
                field: "actions",
                label: this.translate.instant("dwdm.circuit.wizard.intermediates.step.table.columns.actions"),
                width: "10%",
                optionalColumn: {
                    type: ColumnConfigType.ACTIONS,
                    actions: [
                        {
                            id: "remove",
                            iconName: "remove",
                            isDisabled: () => {
                                return !this.model.useIntermediateDevices;
                            },
                            onAction: (row) => {
                                const device = this.model.devices.find(d => d.name === row["name"]);
                                if (device) {
                                    this.confirmRemoveDevice(device);
                                }
                            },
                            getTooltip: () => {
                                return this.translate.instant("buttons.deleteEntry");
                            }
                        }
                    ]
                },
            }
        ];
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    goingToPreviousStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    async comingFromNextStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const model: Promise<NmsWizardStepModel> = this.resolveModel();
        await this.updateDiagramModel(wizardStepsCacheMap);

        return model;
    }

    async comingFromPreviousStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        this.model = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel || new IntermediatesStepModel();
        this.endsModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel;
        this.transpondersModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        this.linksModel = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel;

        this.aEndResourceId = this.endsModel.aEndDevice.id;
        this.zEndResourceId = this.endsModel.zEndDevice.id;

        await this.initializeTableColumns();
        await this.intermediatesStepService.loadLocations();

        this.updateTableData();
        await this.updateDiagramModelWithCurrentValues();
        this.stepLoaded = true;

        return this.resolveModel();
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        if (this.model.useIntermediateDevices) {
            const intermediatesData = this.circuitWizardValidationUtilsService.buildIntermediatesData(this.model,
                this.aEndResourceId, this.zEndResourceId);

            return this.validate().then(() => this.circuitValidationService.validateIntermediates(intermediatesData));
        }

        return this.validate();
    }

    async allowFinish(): Promise<NmsWizardStepValidationResponse> {
        return this.validate();
    }

    validate(): Promise<NmsWizardStepValidationResponse> {
        if (this.model.useIntermediateDevices && !this.hasDevices()) {
            const errorMessage = this.translate.instant("dwdm.circuit.wizard.intermediates.step.validator.no.devices");
            return Promise.reject(new NmsWizardStepValidationResponse(false, errorMessage));
        }
        return Promise.resolve(new NmsWizardStepValidationResponse(true));
    }

    resolveModel(): Promise<NmsWizardStepModel> {
        return Promise.resolve(this.model);
    }

    addDevice(): void {
        if (!this.model.useIntermediateDevices || !this.model) {
            return;
        }

        const onConfirm = (selectedDevices) => {
            this.lastChangeToBeApplied = {
                change: "addIntermediate",
                values: selectedDevices
            };
            this.emitChangesWereMade(this.getCurrentStepModel());
        };

        this.deviceUtilsService.openDevicesModal(this.activeDevices, onConfirm);
    }

    confirmRemoveDevice(intermediateDevice: IntermediateDevice): void {
        const confirmMessage = "dwdm.circuit.wizard.intermediates.step.confirm.remove.device";
        const onConfirm = () => {
            this.lastChangeToBeApplied = {
                change: "removeIntermediate",
                values: [intermediateDevice]
            };
            this.emitChangesWereMade(this.getCurrentStepModel());
        };
        this.showConfirmationDialog(confirmMessage, onConfirm);
    }

    confirmRemoveAllDevices(): void {
        if (!this.hasDevices()) {
            return;
        }

        const confirmMessage = "dwdm.circuit.wizard.intermediates.step.confirm.remove.all.devices";
        const onConfirm = () => {
            this.lastChangeToBeApplied = {
                change: "removeAllIntermediate"
            };
            this.emitChangesWereMade(this.getCurrentStepModel());
        };
        this.showConfirmationDialog(confirmMessage, onConfirm);
    }

    hasDevices(): boolean {
        return !!(this.model && this.model.devices && this.model.devices.length > 0);
    }

    updateTableData(): void {
        if (!this.model || this.model?.devices.length === 0) {
            this.tableData = [];
            this.activeDevices = []
            return;
        }

        this.tableData = this.model.devices.map(device => ({
            name: device.name,
            hostname: device.hostname,
            serialNumber: device.serialNumber,
            firmware: device.firmware,
            location: device.location,
            lastUpdate: device.lastUpdate,
            status: device.status
        }));
    }

    onUseIntermediateChange(value: boolean, useIntermediateToggle: NmsToggleComponent) {
        this.pendingModel.useIntermediateDevices = value;
        useIntermediateToggle.value = this.model.useIntermediateDevices;

        this.lastChangeToBeApplied = {
            change: "changeUseIntermediate",
            values: [value]
        };
        this.emitChangesWereMade(this.getCurrentStepModel());
    }

    async processChanges(cache: NmsWizardStepMapData): Promise<void> {
        if (this.lastChangeToBeApplied) {
            await this.changesCallbacks[this.lastChangeToBeApplied.change](this.lastChangeToBeApplied.values);
            this.lastChangeToBeApplied = null;
        }

        this.updateDiagramModel(cache);
        this.updateStepsModel(cache);
    }

    private async updateDiagramModel(cache: NmsWizardStepMapData): Promise<void> {
        await this.resolveModel();
        this.circuitDiagram = this.diagramConverterService.convert(cache);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private async updateDiagramModelWithCurrentValues(): Promise<void> {
        await this.resolveModel();
        const stepsModel: NmsWizardStepMapData = {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.model,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        };
        this.circuitDiagram = this.diagramConverterService.convert(stepsModel);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    /**
     * Realiza a atualização dos dados do step após um processamento de mudanças.
     * Quando ocorre invalidação dos steps posteriores, as propriedades que armazenam os
     * dados precisam refletir os novos valores.
     */
    private updateStepsModel(cache: NmsWizardStepMapData): Promise<void> {
        this.endsModel = cache["EndsStepModel"] as EndsStepModel;
        this.transpondersModel = cache["TranspondersStepModel"] as TranspondersStepModel;
        this.model = cache["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = cache["LinksStepModel"] as LinksStepModel;
        this.amplifiersModel = cache["AmplifiersStepModel"] as AmplifiersStepModel;
        this.oscsModel = cache["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = cache["GeneralStepModel"] as GeneralStepModel;

        return Promise.resolve();
    }

    /**
     * Retorna os dados atuais.
     * Como a cache @see NmsWizardStepMapData é atualizada apenas no botão próximo do diagrama,
     * é preciso pegar os dados atuais para garantir o funcionamento correto do processChanges.
     */
    private getCurrentStepModel() {
        return {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.model,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.oscsModel,
            "GeneralStepModel": this.generalModel
        };
    }

    private showConfirmationDialog(confirmMessage: string, onConfirm: () => void, onCancel?: () => void): Promise<boolean> {
        const confirmationModalFn = () => true;

        return new Promise<boolean>((resolve) => {
            const confirmationCallbackFn = () => {
                onConfirm();
                resolve(true);
            };
            const cancelCallbackFn = () => {
                if (onCancel) {
                    onCancel();
                }
                resolve(false);
            };

            this.dialogService.confirm(
                confirmationModalFn,
                confirmMessage,
                confirmationCallbackFn,
                cancelCallbackFn
            );
        });
    }

    hasChanges(): boolean {
        return this.lastChangeToBeApplied != null;
    }

    getDependenciesStepsNames(): string[] {
        return [
            "LinksStepModel",
            "AmplifiersStepModel",
            "OscManagementStepModel"
        ];
    }

    private addIntermediate(values: any) {
        const selectedDevices = values;

        selectedDevices.forEach((device: Device) => {
            if (device && device.id) {
                const intermediateDevice: IntermediateDevice = {
                    resourceId: device.id,
                    name: device.name,
                    hostname: device.hostname,
                    serialNumber: device.serialNumber,
                    firmware: device.fwVersion,
                    locationId: device.locationId,
                    location: "",
                    lastUpdate: device.lastUpdate,
                    status: device.status
                };

                this.model.devices.push(intermediateDevice);
                this.activeDevices.push(device);

                this.intermediatesStepService.enrichDeviceWithLocation(intermediateDevice);
            }
        });
        this.updateTableData();
    }

    private removeIntermediate(values: any) {
        const intermediateDevice = values[0];

        const result = this.intermediatesStepService.removeDevice(
            this.model.devices,
            this.activeDevices,
            intermediateDevice
        );

        this.model.devices = result.devices;
        this.activeDevices = result.activeDevices;
        this.updateTableData();
    }

    private removeAllIntermediate() {
        const result = this.intermediatesStepService.removeAllDevices();
        this.model.devices = result.devices;
        this.activeDevices = result.activeDevices;
        this.updateTableData();
    }

    private changeUseIntermediate(values: any) {
        const useIntermediateDevices = values[0];
        this.model.useIntermediateDevices = useIntermediateDevices;
    }
}
